import type { Theme } from '@material-ui/core'
import { makeStyles } from '@material-ui/core'
import type { Moment } from 'moment'
import { DATE_PICKER_VIEWPORT_PADDING } from './datePickerPopperProps'

const IMPORTANT_SUFFIX = ' !important'

// Fallback is tall enough for the largest month (6 rows × 45px daySize +
// 7 × 16px border-spacing) so the picker doesn't clip before it's measured.
export const DAY_PICKER_FALLBACK_HEIGHT = '470px !important'

/**
 * Resolves the `.DayPicker_transitionContainer` height for the date picker.
 *
 * The measured `tableHeight` (the visible month's `offsetHeight`) is the exact
 * content height, so it must be passed through **unmodified** — callers that
 * subtracted a fudge factor clipped the last week row into the day cells on
 * narrow (iPad) viewports (#21206). Returns the fallback until a height has
 * been measured (`tableHeight` falsy / 0).
 */
export function getDayPickerTransitionHeight(tableHeight?: number): string {
  return tableHeight ? `${tableHeight}px !important` : DAY_PICKER_FALLBACK_HEIGHT
}

export interface StylesArgs {
  startDate?: Moment
  endDate?: Moment
  isSingle?: boolean
  tableHeight?: number
  blockPastDays?: boolean
  disableTodayOutline?: boolean
}

export const useDatePickerStyles = makeStyles<Theme, StylesArgs>((theme: Theme) => ({
  tooltipTypography: {
    fontSize: '0.75rem',
    fontWeight: '500',
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.65rem'
    }
  },
  toolTipWrapper: {
    display: 'flex',
    alignContent: 'center',
    justifyContent: 'flex-start',
    paddingBottom: theme.spacing(0.5),
    marginTop: `-${theme.spacing(0.5)}px`
  },
  popper: {
    backgroundColor: theme.palette.background.paper,
    zIndex: 1400,
    boxShadow: theme.shadows[4],
    marginTop: theme.spacing(0.25),
    borderRadius: theme.borderRadius.md,
    maxWidth: 'min-content',
    '& .CalendarMonth_table': {
      borderCollapse: 'separate',
      borderSpacing: theme.spacing(0, 2)
    },
    '& .CalendarMonth_caption': {
      padding: theme.spacing(1.5, 5, 1, 5),
      display: 'flex',
      justifyContent: 'space-between',
      maxWidth: '314px',
      '& > *': {
        margin: theme.spacing(0.5),
        marginBottom: ({ isSingle }) => (isSingle ? theme.spacing(2) : undefined)
      },
      [theme.breakpoints.down('md')]: {
        padding: theme.spacing(1.5, 3, 1, 3)
      }
    },
    '& .DayPicker': (props) => ({
      overflow: 'hidden',
      ...(props.isSingle
        ? {}
        : {
            width: `753px !important`
          })
    }),
    '& .DayPicker_weekHeader': {
      color: theme.palette.text.primary,
      '& ul': {
        ...theme.typography.h4,
        fontWeight: 400,
        textTransform: 'uppercase',
        fontSize: '14px !important'
      },
      paddingTop: ({ isSingle }) => (isSingle ? '8px !important' : undefined)
    },
    '& .DayPicker_transitionContainer': {
      // Use the measured visible-month height. react-dates' own inline height
      // calc is based on raw daySize and ignores our 16px row border-spacing,
      // so trusting it clips 6-row months. See getDayPickerTransitionHeight.
      height: ({ tableHeight }) => getDayPickerTransitionHeight(tableHeight),
      // Single-date mode needs `relative` so the absolutely-positioned
      // CalendarMonthGrid is contained here. Range mode keeps the legacy
      // `revert` (→ static) positioning intentionally — the reported bug
      // only affects single-date pickers, and changing range mode would
      // widen the blast radius beyond what was tested.
      position: ({ isSingle }) => (isSingle ? 'relative' : 'revert'),
      overflow: 'hidden'
    },
    '& .CalendarMonthGrid_month__hidden': {
      height: '0px !important'
    },
    '& .CalendarMonthGrid__horizontal': {
      width: 'initial !important'
    },
    '& .CalendarDay': {
      color: theme.palette.text.primary,
      borderRadius: theme.borderRadius.md,
      border: 'none',
      fontWeight: 500,
      '&:hover': {
        backgroundColor: ({ isSingle }) => (isSingle ? 'transparent' : undefined)
      }
    },
    '& .CalendarDay__outside:not(.CalendarDay__selected)': {
      color: ({ blockPastDays }) => (blockPastDays ? theme.palette.text.primary : theme.palette.text.disabled)
    },
    '& .CalendarDay__blocked_calendar': {
      backgroundColor: 'transparent !important',
      pointerEvents: 'none'
    },
    '& .CalendarDay__blocked_calendar:not(.CalendarDay__selected)': {
      color: `${theme.palette.text.disabled} !important`
    },
    '& .CalendarDay__today:not(.CalendarDay__selected):not(.CalendarDay__selected_span)': {
      border: ({ isSingle, disableTodayOutline }) =>
        isSingle || disableTodayOutline ? 'none' : `1px solid ${theme.paintscout.palette.primary}`
    },
    '& .CalendarDay__selected': {
      color: theme.palette.primary.contrastText,
      backgroundColor: ({ isSingle }) => (isSingle ? 'transparent' : theme.palette.primary.main + IMPORTANT_SUFFIX),
      borderRadius: theme.borderRadius.md + IMPORTANT_SUFFIX
    },
    '& .CalendarDay__selected_start, & .CalendarDay__selected_end': {
      borderRadius: theme.borderRadius.md
    },
    '& .CalendarDay__selected_start:not(:last-child)': {
      position: 'relative',
      borderRadius: ({ endDate }) =>
        !endDate ? theme.borderRadius.md : `${theme.borderRadius.md} 0 0 ${theme.borderRadius.md}`,
      backgroundColor: ({ startDate, endDate }) =>
        startDate?.day() === endDate?.day()
          ? theme.palette.background.paper
          : theme.paintscout.palette.brand.Neutral.P94,
      '&::before': {
        content: ({ startDate }) => `'${startDate ? startDate.format('D') : ''}'`,
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        backgroundColor: theme.palette.primary.main,
        borderRadius: theme.borderRadius.md,
        zIndex: 1,
        display: ({ startDate }) => (startDate ? 'flex' : 'none'),
        justifyContent: 'center',
        alignItems: 'center'
      }
    },
    '& .CalendarDay__selected_end:not(:first-child)': {
      position: 'relative',
      borderRadius: ({ startDate }) => (!startDate ? '50%' : '0 50% 50% 0'),
      backgroundColor: ({ startDate, endDate }) =>
        startDate?.day() === endDate?.day()
          ? theme.palette.background.paper
          : theme.paintscout.palette.brand.Neutral.P94,
      '&::before': {
        content: ({ endDate }) => `'${endDate ? endDate.format('D') : ''}'`,
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        backgroundColor: theme.palette.primary.main,
        borderRadius: theme.borderRadius.md,
        zIndex: 1,
        display: ({ endDate }) => (endDate ? 'flex' : 'none'),
        justifyContent: 'center',
        alignItems: 'center'
      }
    },
    '& .CalendarDay__selected_span': {
      backgroundColor: ({ isSingle }) =>
        isSingle ? theme.palette.background.paper : theme.paintscout.palette.brand.Neutral.P94,
      borderRadius: 0,
      '&:first-child': {
        borderRadius: `${theme.borderRadius.md} 0 0 ${theme.borderRadius.md}`
      },
      '&:last-child': {
        borderRadius: `0 ${theme.borderRadius.md} ${theme.borderRadius.md} 0`
      }
    },
    '& .CalendarDay__hovered_span': {
      background: 'none',
      borderRadius: 0,
      '&:first-child': {
        borderRadius: `${theme.borderRadius.md} 0 0 ${theme.borderRadius.md}`
      },
      '&:last-child': {
        borderRadius: `0 ${theme.borderRadius.md} ${theme.borderRadius.md} 0`
      },
      '&:hover': {
        borderRadius: theme.borderRadius.md,
        background: theme.paintscout.palette.brand.Neutral.P94
      }
    },
    '& .CalendarDay__highlighted_calendar:not(.CalendarDay__selected):not(.CalendarDay__selected_span)': {
      background: 'none',
      '&:hover': {
        background: theme.paintscout.palette.brand.Neutral.P94
      }
    },
    '& .CalendarDay__highlighted_calendar.CalendarDay__selected_span': {
      color: theme.palette.primary.main
    },
    '& .DayPickerKeyboardShortcuts_buttonReset': {
      display: 'none'
    }
  },
  // Desktop popper only (not the mobile Dialog). With preventOverflow clamping
  // the popper to the viewport, this cap lets a calendar taller than the window
  // scroll instead of hiding its last weeks (#24468).
  popperScroll: {
    maxHeight: `calc(100vh - ${DATE_PICKER_VIEWPORT_PADDING * 2}px)`,
    overflowY: 'auto',
    overflowX: 'hidden'
  },
  dialogMargin: {
    margin: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
      margin: 0
    }
  },
  paper: {
    position: 'relative',
    paddingTop: theme.spacing(2),
    border: 'none',
    boxShadow: 'none',
    [theme.breakpoints.down('md')]: {
      display: 'flex',
      justifyContent: 'center'
    },
    [theme.breakpoints.down('sm')]: {
      paddingTop: 0
    }
  },
  navButton: {
    position: 'absolute',
    top: '28px',
    padding: 0,
    left: ({ isSingle }) => (isSingle ? '34px' : '35px'),
    right: 'auto',
    [theme.breakpoints.down('md')]: {
      top: ({ isSingle }) => (isSingle ? undefined : '28px'),
      left: ({ isSingle }) => (isSingle ? '30px' : '24px')
    },
    [theme.breakpoints.down('sm')]: {
      top: ({ isSingle }) => (isSingle ? '26px' : undefined),
      left: ({ isSingle }) => (isSingle ? '26px' : '26px')
    }
  },
  rightNavButton: {
    right: ({ isSingle }) => (isSingle ? '33px' : '35px') + IMPORTANT_SUFFIX,
    left: 'auto' + IMPORTANT_SUFFIX,
    [theme.breakpoints.down('md')]: {
      right: ({ isSingle }) => (isSingle ? '31px !important' : '24px !important')
    },
    [theme.breakpoints.down('sm')]: {
      right: ({ isSingle }) => (isSingle ? '29px !important' : '26px !important')
    }
  },
  topContent: {
    padding: theme.spacing(3, 3, 2, 3)
  },
  monthTitles: {
    position: 'absolute',
    display: 'flex',
    justifyContent: 'space-between',
    top: '26px',
    left: 'calc(25% - 20px)',
    right: 'calc(25% - 5px)',
    [theme.breakpoints.down('md')]: {
      left: 'auto',
      right: 'auto',
      width: '445px'
    },
    zIndex: 2,
    '& > p': {
      ...theme.typography.h6,
      textTransform: 'uppercase',
      fontSize: '0.9rem',
      fontWeight: 500
    }
  },
  dialogContent: {
    padding: 0,
    overflowX: 'hidden',
    overflowY: 'hidden',
    '@media screen and (max-height: 930px)': {
      overflowY: 'auto'
    }
  },
  mobileInputs: {
    padding: theme.spacing(2, 3),
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  input: {
    ...theme.typography.body1,
    cursor: 'pointer !important',
    '& input': {
      cursor: 'pointer !important'
    },
    '& svg': {
      fontSize: '1rem'
    }
  },
  headerCardInput: {
    border: 'none !important',
    boxShadow: 'none !important',
    '& input': {
      marginBottom: theme.spacing(2),
      padding: 0
    }
  },
  selectedInput: {
    borderColor: theme.palette.primary.main,
    '& input': {
      boxShadow: `inset 0 0 0 2px ${theme.palette.primary.main} !important`,
      '-webkit-box-shadow': `inset 0 0 0 2px ${theme.palette.primary.main}!important`,
      '-webkit-border-radius': '0px',
      '-webkit-appearance': 'none'
    },
    '&:hover': {
      backgroundColor: 'inherit',
      boxShadow: 'none'
    }
  },
  mobileFooter: {
    width: '100%'
  }
}))
